import { capitalizeAll } from '@moderepo/mode-app-components';
import { createAlertRule, updateAlertRule, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import React, { CSSProperties, useCallback, useContext, useMemo } from 'react';
import { CircularProgress, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { AlertConditionType, CreateAlertRuleParams } from '@moderepo/mode-internal-apis';
import { EditAlertRuleInfoComp, EditAlertRuleInfoCompProps, getAlertConditionTypeDisplayName } from '.';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectAlertRule, useSelectProject } from '../../utils/dataStateHooks';
import { EditAlertRuleResult } from '..';



const useStyle = makeStyles(() => {
    return {
        root: {
            width: '100%',
        },
    };
}, {});


export interface EditAlertRuleInfoContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;

    // targetAlertRule is either an AlertRule object ID (for Edit Alert Rule mode), or an AlertConditionType value (for CREATE Alert Rule mode)
    // or a copy of an AlertRule object which is also used for creating a new alert rule.
    readonly targetAlertRule: number | AlertConditionType | CreateAlertRuleParams;

    // Handler when the Edit Alert rule is done or cancelled.
    readonly onCancelEdit: ()=> void;
    readonly onFinishEdit: ()=> void;
}



/**
 * This container shows the AlertRule editor. It contains the logics for the EditAlertRuleInfoComp.
 */
export const EditAlertRuleInfoContainer: React.FC<EditAlertRuleInfoContainerProps> = (props: EditAlertRuleInfoContainerProps) => {

    const { dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);

    const classes = useStyle();

    // Because props.targetAlertRule can be an ID of an existing Alert Rule or a New Alert Rule type or a clone of an existing alert rule,
    // we need to check to see what it is, existingAlertRuleId or newAlertRuleType or alertRuleClone, before we can use it.
    const existingAlertRuleId = typeof props.targetAlertRule === 'number' ? props.targetAlertRule : undefined;
    const newAlertRuleType = typeof props.targetAlertRule === 'string' ? props.targetAlertRule : undefined;
    const alertRuleClone = typeof props.targetAlertRule === 'object' ? props.targetAlertRule : undefined;


    // Get the alert rule object for the specified existingAlertRuleId
    const selectedAlertRule = useSelectAlertRule(props.projectId, existingAlertRuleId);


    /**
     * On save button clicked handler. This will make API call to Create/Update an alert rule based on the alertRuleResult.
     */
    const onSaveAlertRule = useCallback(async (alertRuleResult: EditAlertRuleResult) => {
        if (alertRuleResult.newAlertRule !== undefined) {

            // The "alertRuleResult" contain "newAlertRule" object. We will make API call to CREATE the new alert rule object
            await appDataDispatch(createAlertRule(props.projectId, alertRuleResult.newAlertRule));
            props.onFinishEdit();

        } else if (alertRuleResult.updatedAlertRule !== undefined) {

            // The alertRuleResult.updatedAlertRule.alertRule contains only the values for the attributes that changed. For attributes
            // that has not changes, the value will be undefined. So we need to check if there is any NON undefined values in the object.
            // If there are at least 1 then we will make API call to update the alert rule. If they are all undefined then nothing changed
            // therefore we will not make API call.
            const hasUpdatedAttributes = Object.values(alertRuleResult.updatedAlertRule.alertRule).some((value) => {
                return value !== undefined;
            });

            if (hasUpdatedAttributes) {
                await appDataDispatch(updateAlertRule(
                    props.projectId, alertRuleResult.updatedAlertRule.alertRuleId, alertRuleResult.updatedAlertRule.alertRule),
                );
            }
            props.onFinishEdit();
        }
    }, [appDataDispatch, props]);


    /**
     * Create and cache the actions required for the Edit Alert component
     */
    const editAlertCompActions = useMemo((): EditAlertRuleInfoCompProps['actions'] => {
        return {
            cancelEdit: {
                label  : capitalizeAll(trans('label.cancel')),
                onClick: props.onCancelEdit,
            },
            saveUpdate: {
                label  : capitalizeAll(newAlertRuleType || alertRuleClone ? trans('label.create') : trans('label.save')),
                onClick: onSaveAlertRule,
            },
        };
    }, [alertRuleClone, newAlertRuleType, onSaveAlertRule, props.onCancelEdit, trans]);


    /**
     * Create and cache the title to be displayed in the component header. The title to be displayed will be based on whether we are showing
     * this component for EDITING an existing alert rule or CREATE new alert rule.
     */
    const compTitle = useMemo(() => {
        // If newAlertRuleType or alertRuleClone is provided, then show the CREATE alert rule title
        if (newAlertRuleType || alertRuleClone) {
            const conditionType = newAlertRuleType ?? alertRuleClone?.condition.conditionType as AlertConditionType;
            return trans('alerting.create_alert_rule_panel.title', {
                alert_rule_type: trans(getAlertConditionTypeDisplayName(conditionType)),
            });
        }

        // else, show EDIT alert rule title
        return trans('alerting.edit_alert_rule_panel.title');
    }, [alertRuleClone, newAlertRuleType, trans]);


    return (
        <>
            {(!existingAlertRuleId || selectedAlertRule) && (
                <EditAlertRuleInfoComp
                    className={clsx(classes.root, 'editing')}
                    isEditing
                    style={props.style}
                    title={capitalizeAll(compTitle)}
                    icon="notifications"
                    actions={editAlertCompActions}
                    projectId={props.projectId}
                    targetAlertRule={selectedAlertRule ?? alertRuleClone ?? newAlertRuleType as AlertConditionType}
                />
            )}

            {existingAlertRuleId && !selectedAlertRule && (
                <CircularProgress />
            )}
        </>
    );
};
